import { useState, useEffect, useCallback } from 'react';
import { usePlanLimits } from '../../../hooks/usePlanLimits';
import { useSubscription } from '../../../hooks/useSubscription';
import { CheckoutModal } from '../../../components/feature/CheckoutModal';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { PlanDiagnosticCard } from './PlanDiagnosticCard';

interface PlanTabProps {
  onUpgrade: () => void;
}

interface PlanRow {
  id: string;
  name: string;
  slug: string;
  price_brl: number;
  periodicity: string;
  accounts_limit: number;
  cards_limit: number;
  monthly_transactions_limit: number;
  goals_limit: number;
  reports_enabled: boolean;
  is_featured: boolean;
  display_order: number;
}

interface PaymentRow {
  id: string;
  status: string;
  amount: number;
  gateway: string | null;
  payment_method: string | null;
  gateway_transaction_id: string | null;
  gateway_response: string | null;
  paid_at: string | null;
  created_at: string;
  plans?: { name: string } | null;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatLimit = (v: number): string => (v === -1 ? 'Ilimitado' : String(v));

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' } as Intl.DateTimeFormatOptions);
};

const getPaymentStatusMeta = (status: string) => {
  switch (status) {
    case 'approved': return { label: 'Aprovado', color: 'text-emerald-600 bg-emerald-50', icon: 'ri-checkbox-circle-fill', dot: 'bg-emerald-400' };
    case 'pending': return { label: 'Em análise', color: 'text-yellow-600 bg-yellow-50', icon: 'ri-time-fill', dot: 'bg-yellow-400' };
    case 'failed': return { label: 'Recusado', color: 'text-red-600 bg-red-50', icon: 'ri-close-circle-fill', dot: 'bg-red-400' };
    case 'refunded': return { label: 'Estornado', color: 'text-gray-600 bg-gray-100', icon: 'ri-refund-2-line', dot: 'bg-gray-400' };
    default: return { label: status, color: 'text-gray-600 bg-gray-100', icon: 'ri-information-line', dot: 'bg-gray-300' };
  }
};

const getSubStatusMeta = (status: string | undefined) => {
  switch (status) {
    case 'active': return { label: 'Ativa', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
    case 'cancelled': return { label: 'Cancelada', color: 'text-red-700 bg-red-50 border-red-200' };
    case 'expired': return { label: 'Expirada', color: 'text-gray-600 bg-gray-100 border-gray-200' };
    case 'pending_activation': return { label: 'Aguardando', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' };
    case 'payment_failed': return { label: 'Pagamento falhou', color: 'text-red-700 bg-red-50 border-red-200' };
    default: return { label: status ?? 'Desconhecido', color: 'text-gray-600 bg-gray-100 border-gray-200' };
  }
};

export const PlanTab = ({ onUpgrade }: PlanTabProps) => {
  const {
    checkAccountLimit, checkCardLimit, checkGoalLimit,
    getTransactionCount, monthlyTransactionLimit, isPaid, plan,
  } = usePlanLimits();
  const { subscription, refetch } = useSubscription();
  const { user } = useAuth();

  const [txCount, setTxCount] = useState(0);
  const [txLoading, setTxLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [allPlans, setAllPlans] = useState<PlanRow[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);

  useEffect(() => {
    getTransactionCount().then(c => { setTxCount(c); setTxLoading(false); });
  }, [getTransactionCount]);

  const fetchPayments = useCallback(async () => {
    if (!user) return;
    setPaymentsLoading(true);
    const { data } = await supabase
      .from('payments')
      .select('id, status, amount, gateway, payment_method, gateway_transaction_id, gateway_response, paid_at, created_at, plans(name)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);
    setPayments((data as PaymentRow[]) || []);
    setPaymentsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchPayments();
    supabase.from('plans').select('id, name, slug, price_brl, periodicity, accounts_limit, cards_limit, monthly_transactions_limit, goals_limit, reports_enabled, is_featured, display_order').eq('is_active', true).eq('is_public', true).order('display_order').then(({ data }) => {
      setAllPlans((data as PlanRow[]) || []);
      setPlansLoading(false);
    });
  }, [fetchPayments]);

  const handleCancelAutoRenew = async () => {
    if (!subscription) return;
    setCancelLoading(true);
    await supabase.from('subscriptions').update({ auto_renew: false }).eq('id', subscription.id);
    await refetch();
    setCancelLoading(false);
    setCancelConfirm(false);
  };

  const handleReactivateAutoRenew = async () => {
    if (!subscription) return;
    setCancelLoading(true);
    await supabase.from('subscriptions').update({ auto_renew: true }).eq('id', subscription.id);
    await refetch();
    setCancelLoading(false);
  };

  const handleUpgradeClick = () => { setShowCheckout(true); onUpgrade(); };
  const handleCheckoutSuccess = () => { setShowCheckout(false); refetch(); fetchPayments(); };

  const accountCheck = checkAccountLimit();
  const cardCheck = checkCardLimit();
  const goalCheck = checkGoalLimit();

  const usageItems = [
    { label: 'Contas bancárias', icon: 'ri-bank-line', current: accountCheck.current, limit: isPaid ? null : (accountCheck.limit ?? 1), color: '#10b981' },
    { label: 'Cartões de crédito', icon: 'ri-bank-card-line', current: cardCheck.current, limit: isPaid ? null : (cardCheck.limit ?? 1), color: '#0891b2' },
    { label: 'Metas financeiras', icon: 'ri-focus-3-line', current: goalCheck.current, limit: isPaid ? null : (goalCheck.limit ?? 2), color: '#8b5cf6' },
    { label: 'Lançamentos (mês atual)', icon: 'ri-exchange-funds-line', current: txLoading ? 0 : txCount, limit: isPaid ? null : monthlyTransactionLimit, color: '#f59e0b' },
  ];

  const expiresAt = subscription?.expires_at;
  const startedAt = subscription?.started_at;
  const autoRenew = subscription?.auto_renew ?? true;
  const subStatus = subscription?.status;
  const statusMeta = getSubStatusMeta(subStatus);
  const planPrice = plan?.price_brl ? fmt(Number(plan.price_brl)) : null;
  const billingCycle = subscription?.billing_cycle === 'annual' ? 'anual' : 'mensal';

  const recentPayment = payments[0] ?? null;
  const showPaymentAlert = !isPaid && recentPayment && (recentPayment.status === 'failed' || subStatus === 'payment_failed' || subStatus === 'pending_activation');

  const visiblePayments = showAllPayments ? payments : payments.slice(0, 5);

  // Vigency status: subscription active but auto_renew=false
  const showVigencyBanner = isPaid && !autoRenew && expiresAt;
  const vigencyDate = expiresAt ? new Date(expiresAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

  // Build comparison
  const freePlan = allPlans.find(p => Number(p.price_brl) === 0) || null;
  const paidPlans = allPlans.filter(p => Number(p.price_brl) > 0);
  type CompareRow = { feature: string; getValue: (p: PlanRow) => string | boolean };
  const compareRows: CompareRow[] = [
    { feature: 'Contas bancárias', getValue: p => formatLimit(p.accounts_limit) },
    { feature: 'Cartões de crédito', getValue: p => formatLimit(p.cards_limit) },
    { feature: 'Metas financeiras', getValue: p => formatLimit(p.goals_limit) },
    { feature: 'Lançamentos por mês', getValue: p => formatLimit(p.monthly_transactions_limit) },
    { feature: 'Dashboard completo', getValue: () => true },
    { feature: 'Relatórios avançados', getValue: p => p.reports_enabled },
    { feature: 'Exportar dados (CSV/PDF)', getValue: p => p.reports_enabled },
    { feature: 'Suporte prioritário', getValue: p => Number(p.price_brl) > 0 },
  ];
  const totalCols = 1 + (freePlan ? 1 : 0) + paidPlans.length;
  const gridClass = totalCols === 2 ? 'grid-cols-2' : totalCols === 3 ? 'grid-cols-3' : 'grid-cols-4';

  const totalApproved = payments.filter(p => p.status === 'approved').reduce((a, p) => a + Number(p.amount), 0);

  return (
    <div className="space-y-5">

      {/* Payment alert */}
      {showPaymentAlert && recentPayment && (
        <div className={`rounded-2xl p-4 border-2 flex items-start gap-3 ${recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'border-red-100 bg-red-50' : 'border-yellow-100 bg-yellow-50'}`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'bg-red-100' : 'bg-yellow-100'}`}>
            <i className={`text-base ${recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'ri-close-circle-fill text-red-500' : 'ri-time-fill text-yellow-500'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-700 mb-0.5 ${recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'text-red-700' : 'text-yellow-700'}`}>
              {recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'Última tentativa de pagamento recusada' : 'Pagamento em análise'}
            </p>
            <p className={`text-xs ${recentPayment.status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`}>
              {recentPayment.status === 'failed' || subStatus === 'payment_failed' ? 'Seu pagamento foi recusado. Tente novamente com outro cartão.' : 'Seu pagamento está sendo processado. O plano será ativado automaticamente após aprovação.'}
            </p>
          </div>
          {(recentPayment.status === 'failed' || subStatus === 'payment_failed') && (
            <button onClick={handleUpgradeClick} className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-700 rounded-lg cursor-pointer whitespace-nowrap transition-colors flex-shrink-0">Tentar novamente</button>
          )}
        </div>
      )}

      {/* Vigency banner — subscription active but auto_renew disabled */}
      {showVigencyBanner && (
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
            <i className="ri-calendar-event-line text-amber-600 text-base" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-700 text-amber-800 mb-1">Renovação automática desativada</p>
            <p className="text-sm text-amber-700 leading-relaxed">
              Seu plano <span className="font-700">{plan?.name ?? 'Premium'}</span> continuará ativo até <span className="font-700">{vigencyDate}</span>.
              Após essa data, você voltará automaticamente ao <span className="font-700">Plano Gratuito</span>.
            </p>
            <button
              onClick={handleReactivateAutoRenew}
              disabled={cancelLoading}
              className="mt-2.5 flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-700 rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
            >
              <i className="ri-refresh-line" />Reativar renovação automática
            </button>
          </div>
        </div>
      )}

      {/* Plan banner */}
      <div className={`rounded-2xl p-5 border-2 ${isPaid ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isPaid ? 'bg-amber-100' : 'bg-gray-100'}`}>
              <i className={`text-2xl ${isPaid ? 'ri-vip-crown-fill text-amber-600' : 'ri-user-line text-gray-500'}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-700 text-gray-900">
                  {plan?.name ?? (isPaid ? 'Premium' : 'Plano Gratuito')}
                </p>
                <span className={`text-xs font-700 px-2 py-0.5 rounded-full ${isPaid ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>
                  {isPaid ? 'ATIVO' : 'FREE'}
                </span>
                {subscription && (
                  <span className={`text-xs font-600 px-2 py-0.5 rounded-full border ${statusMeta.color}`}>
                    {statusMeta.label}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                {isPaid
                  ? `${planPrice ? `${planPrice}/${billingCycle === 'anual' ? 'ano' : 'mês'} · ` : ''}Acesso completo ao sistema`
                  : 'Recursos limitados · Upgrade para desbloquear tudo'}
              </p>
              {startedAt && <p className="text-xs text-gray-400 mt-0.5">Ativo desde {fmtDate(startedAt)}</p>}
              {expiresAt && autoRenew && <p className="text-xs text-amber-600 mt-0.5 font-600">Renova em {fmtDate(expiresAt)}</p>}
            </div>
          </div>
          {!isPaid && (
            <button onClick={handleUpgradeClick} className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-rocket-line" />Fazer upgrade
            </button>
          )}
        </div>

        {/* Subscription details grid */}
        {isPaid && subscription && (
          <div className="mt-4 grid grid-cols-3 gap-3 pt-4 border-t border-amber-200/60">
            <div>
              <p className="text-xs text-amber-700/60 mb-0.5">Valor do plano</p>
              <p className="text-sm font-700 text-amber-900">{fmt(Number(subscription.amount))}</p>
              <p className="text-xs text-amber-700/50">{billingCycle}</p>
            </div>
            <div>
              <p className="text-xs text-amber-700/60 mb-0.5">Próxima cobrança</p>
              <p className="text-sm font-700 text-amber-900">{autoRenew ? fmtDate(expiresAt) : '—'}</p>
              {!autoRenew && <p className="text-xs text-amber-700/50">Auto-renovação off</p>}
            </div>
            <div>
              <p className="text-xs text-amber-700/60 mb-0.5">Renovação automática</p>
              <div className="flex items-center gap-2 mt-1">
                <button
                  onClick={() => autoRenew ? setCancelConfirm(true) : handleReactivateAutoRenew()}
                  disabled={cancelLoading}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-700 cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 ${autoRenew ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                >
                  <i className={`text-xs ${autoRenew ? 'ri-checkbox-circle-fill' : 'ri-close-circle-line'}`} />
                  {autoRenew ? 'Ativada' : 'Desativada'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel auto-renew confirm */}
      {cancelConfirm && (
        <div className="bg-white rounded-2xl border-2 border-red-100 p-5">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-red-500 text-base" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-700 text-gray-900 mb-1">Cancelar renovação automática?</p>
              <p className="text-sm text-gray-500 leading-relaxed">
                Você continuará tendo acesso ao plano <span className="font-700">{plan?.name ?? 'Premium'}</span> até <span className="font-700">{fmtDate(expiresAt)}</span>. Após essa data, sua conta voltará ao <span className="font-700">Plano Gratuito</span> automaticamente.
              </p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setCancelConfirm(false)} className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-500 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">
                  Manter ativo
                </button>
                <button onClick={handleCancelAutoRenew} disabled={cancelLoading} className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                  {cancelLoading ? <i className="ri-loader-4-line animate-spin" /> : null}
                  Cancelar renovação
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <p className="text-sm font-700 text-gray-800 mb-4">Uso atual do plano</p>
        <div className="space-y-4">
          {usageItems.map(item => {
            const pct = item.limit ? Math.min(100, (item.current / item.limit) * 100) : 0;
            const isAtLimit = item.limit !== null && item.current >= item.limit;
            const isWarning = item.limit !== null && pct >= 75 && !isAtLimit;
            return (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-lg" style={{ backgroundColor: item.color + '18' }}>
                      <i className={`${item.icon} text-xs`} style={{ color: item.color }} />
                    </div>
                    <span className="text-sm text-gray-700 font-500">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPaid || item.limit === null ? (
                      <span className="text-xs font-600 text-emerald-600 flex items-center gap-1">
                        <i className="ri-infinity-line" />{item.current} usados · Ilimitado
                      </span>
                    ) : (
                      <span className={`text-xs font-700 ${isAtLimit ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-gray-600'}`}>
                        {txLoading && item.icon === 'ri-exchange-funds-line' ? '...' : item.current} / {item.limit}
                      </span>
                    )}
                  </div>
                </div>
                {!isPaid && item.limit !== null && (
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: isAtLimit ? '#ef4444' : isWarning ? '#f59e0b' : item.color }} />
                  </div>
                )}
                {isAtLimit && (
                  <p className="text-xs text-red-500 mt-1 font-500">Limite atingido · <button onClick={handleUpgradeClick} className="underline cursor-pointer font-600">Fazer upgrade para continuar</button></p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment history */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div>
            <p className="text-sm font-700 text-gray-800">Histórico de pagamentos</p>
            {payments.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">
                {payments.filter(p => p.status === 'approved').length} aprovados · Total {fmt(totalApproved)}
              </p>
            )}
          </div>
          {payments.length > 5 && (
            <button onClick={() => setShowAllPayments(v => !v)} className="text-xs text-emerald-600 font-600 cursor-pointer hover:text-emerald-700 whitespace-nowrap">
              {showAllPayments ? 'Ver menos' : `Ver todos (${payments.length})`}
            </button>
          )}
        </div>

        {paymentsLoading ? (
          <div className="px-5 pb-5 space-y-2">
            {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : payments.length === 0 ? (
          <div className="px-5 pb-5 text-center py-8">
            <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <i className="ri-receipt-line text-gray-300 text-xl" />
            </div>
            <p className="text-sm text-gray-400">Nenhum pagamento encontrado</p>
          </div>
        ) : (
          <div className="px-5 pb-5">
            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-xl">
              {(['approved', 'pending', 'failed'] as const).map(st => {
                const count = payments.filter(p => p.status === st).length;
                const meta = getPaymentStatusMeta(st);
                return (
                  <div key={st} className="text-center">
                    <p className={`text-lg font-700 ${meta.color.split(' ')[0]}`}>{count}</p>
                    <p className="text-xs text-gray-400">{meta.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              {visiblePayments.map(payment => {
                const meta = getPaymentStatusMeta(payment.status);
                let failureReason: string | null = null;
                if (payment.status === 'failed' && payment.gateway_response) {
                  try {
                    const gw = JSON.parse(payment.gateway_response);
                    failureReason = gw?.failure_reason ?? gw?.status_detail ?? null;
                  } catch {
                    // ignore parse error
                  }
                }
                return (
                  <div key={payment.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color.split(' ')[1]}`}>
                      <i className={`${meta.icon} text-sm ${meta.color.split(' ')[0]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-600 text-gray-800 truncate">{payment.plans?.name || 'Plano'}</p>
                        <span className={`text-[11px] font-600 px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-gray-400">{fmtDateTime(payment.paid_at || payment.created_at)}</p>
                        {payment.gateway && <span className="text-xs text-gray-300">·</span>}
                        {payment.gateway && <p className="text-xs text-gray-400 capitalize">{payment.gateway}</p>}
                        {payment.payment_method && <span className="text-xs text-gray-300">·</span>}
                        {payment.payment_method && <p className="text-xs text-gray-400 capitalize">{payment.payment_method}</p>}
                      </div>
                      {failureReason && (
                        <p className="text-xs text-red-500 mt-0.5">{failureReason}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-700 text-gray-900">{fmt(Number(payment.amount))}</p>
                      {payment.gateway_transaction_id && (
                        <p className="text-[10px] text-gray-300 mt-0.5 font-mono truncate max-w-24" title={payment.gateway_transaction_id}>
                          #{payment.gateway_transaction_id.slice(-8)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Feature comparison */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <p className="text-sm font-700 text-gray-800">Comparação de recursos</p>
        </div>
        {plansLoading ? (
          <div className="px-5 pb-5 space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : (
          <div className="px-5 pb-2 overflow-x-auto">
            <div className={`grid gap-0 text-xs font-700 text-gray-400 uppercase tracking-wide pb-2 border-b border-gray-100 ${gridClass}`}>
              <span>Recurso</span>
              {freePlan && <span className="text-center">{freePlan.name}</span>}
              {paidPlans.map(p => (
                <span key={p.id} className={`text-center ${p.is_featured ? 'text-amber-600' : 'text-emerald-600'}`}>{p.name}</span>
              ))}
            </div>
            <div className="divide-y divide-gray-50">
              {compareRows.map(row => (
                <div key={row.feature} className={`grid gap-0 py-2.5 items-center ${gridClass}`}>
                  <span className="text-sm text-gray-700">{row.feature}</span>
                  {freePlan && (
                    <div className="flex justify-center">
                      {(() => { const val = row.getValue(freePlan); return typeof val === 'boolean' ? <i className={`text-sm ${val ? 'ri-check-line text-emerald-500' : 'ri-close-line text-gray-300'}`} /> : <span className="text-xs text-gray-500">{val}</span>; })()}
                    </div>
                  )}
                  {paidPlans.map(p => (
                    <div key={p.id} className="flex justify-center">
                      {(() => { const val = row.getValue(p); return typeof val === 'boolean' ? <i className={`text-sm ${val ? 'ri-check-line text-emerald-500' : 'ri-close-line text-gray-300'}`} /> : <span className="text-xs font-600 text-emerald-700">{val}</span>; })()}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {!plansLoading && (
              <div className={`grid gap-0 py-3 border-t border-gray-100 items-center ${gridClass}`}>
                <span className="text-xs font-700 text-gray-500 uppercase tracking-wide">Valor</span>
                {freePlan && <div className="text-center"><span className="text-sm font-700 text-gray-500">Grátis</span></div>}
                {paidPlans.map(p => (
                  <div key={p.id} className="text-center">
                    <span className="text-sm font-700 text-emerald-700">R$ {Number(p.price_brl).toFixed(2).replace('.', ',')}</span>
                    <span className="text-xs text-gray-400 block">{p.periodicity === 'annual' ? '/ano' : '/mês'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!isPaid && (
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-700 text-gray-800">Faça upgrade agora</p>
                <p className="text-xs text-gray-500">Acesso completo · Cancele quando quiser</p>
              </div>
              <button onClick={handleUpgradeClick} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-700 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-vip-crown-line" />Ver planos
              </button>
            </div>
          </div>
        )}
        {isPaid && (
          <div className="px-5 py-4 bg-emerald-50 border-t border-emerald-100">
            <div className="flex items-center gap-3">
              <i className="ri-shield-check-line text-emerald-500 text-lg" />
              <p className="text-sm text-emerald-700 font-600">Você tem acesso completo a todos os recursos. Obrigado por apoiar o produto!</p>
            </div>
          </div>
        )}
      </div>

      <CheckoutModal open={showCheckout} onClose={() => setShowCheckout(false)} onSuccess={handleCheckoutSuccess} />

      {/* ── Plan Diagnostic ─────────────────────────────────────────────── */}
      <PlanDiagnosticCard />
    </div>
  );
};
